import { imgAt, heroSrcSet } from '../mock';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';

// Small eyebrow label used across pages
export const SectionLabel = ({ children, className = '' }) => (
  <span
    className={`inline-block text-[13px] font-semibold tracking-[0.18em] uppercase text-[#e0564e] ${className}`}
  >
    {children}
  </span>
);

// Decorative rainbow bar used on top of cards
export const RainbowTop = () => (
  <span className="block h-[5px] w-full card-rainbow-top" />
);

// Page hero used on interior guide pages
export const PageHero = ({ image, alt, label, title, subtitle, meta }) => (
  <section className="relative">
    <div className="relative h-[52vh] min-h-[380px] w-full overflow-hidden">
      <img src={imgAt(image, 1920, '21:9')} srcSet={heroSrcSet(image, '21:9')} sizes="100vw" alt={alt || title} loading="eager" fetchpriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover object-center" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1410]/95 via-[#1A1410]/35 to-[#1A1410]/5" />
      <div className="relative max-w-4xl mx-auto px-5 h-full flex flex-col justify-end pb-10">
        <span className="inline-block w-max text-[12px] font-semibold tracking-[0.18em] uppercase text-[#CBA344]">
          {label}
        </span>
        <h1 className="mt-3 font-display text-[34px] sm:text-[46px] leading-[1.08] font-semibold text-white max-w-3xl">
          {title}
        </h1>
      </div>
    </div>
    {(subtitle || meta) && (
      <div className="max-w-3xl mx-auto px-5 pt-8">
        {subtitle && (
          <p className="text-[19px] leading-relaxed text-[#3a4f63]">{subtitle}</p>
        )}
        {meta && (
          <div className="mt-6 flex items-center gap-3 text-[14px] text-[#6a7d8e]">
            <span className="flex items-center justify-center w-9 h-9 rounded-full sunset-gradient text-white text-[12px] font-bold">
              UG
            </span>
            <span>{meta}</span>
          </div>
        )}
      </div>
    )}
  </section>
);

// Accordion FAQ list
export const FAQList = ({ faqs }) => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="divide-y divide-[#E6D7A2] rounded-2xl border border-[#E6D7A2] bg-[#EFE7D2] overflow-hidden">
      {faqs.map((f, i) => {
        const isOpen = openIdx === i;
        return (
          <div key={i}>
            <button
              onClick={() => setOpenIdx(isOpen ? -1 : i)}
              className="w-full flex items-center justify-between gap-4 text-left px-6 py-5 hover:bg-[#fbf2e3] transition-colors"
            >
              <span className="font-display text-[18px] font-semibold text-[#1B1D21]">
                {f.q}
              </span>
              <ChevronDown
                className={`w-5 h-5 shrink-0 text-[#e0564e] transition-transform duration-300 ${
                  isOpen ? 'rotate-180' : ''
                }`}
              />
            </button>
            <div
              className={`overflow-hidden transition-[max-height,opacity] duration-300 ${
                isOpen ? 'max-h-60 opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <p className="px-6 pb-5 text-[16px] leading-relaxed text-[#45596b]">
                {f.a}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Affiliate disclosure callout
export const DisclosureNote = ({ children }) => (
  <div className="rounded-xl border-l-4 border-[#CBA344] bg-[#F2ECCF] px-5 py-4 text-[14px] leading-relaxed text-[#5D3B26]">
    {children}{' '}
    <Link to="/about" className="font-semibold underline underline-offset-2 text-[#92722A]">
      full disclosure
    </Link>
    .
  </div>
);

// Text link with arrow used in cards
export const ArrowLink = ({ to, children }) => (
  <Link
    to={to}
    className="group/al inline-flex items-center gap-1.5 text-[15px] font-semibold text-[#2F663C] hover:text-[#1B1D21] transition-colors"
  >
    {children}
    <span className="transition-transform duration-300 group-hover/al:translate-x-1">→</span>
  </Link>
);
